import mathHelper from '../Helpers/mathHelper';
import moneyHelper from '../Helpers/moneyHelper';
import TierListForOverallHelper from '../Helpers/ModelHelpers/TierListForOverallHelper';

// --- CONSTANTS ---
const DEFAULT_PERCENT_STRING = '0%';
const SERVICE_TYPE_CODES_DEFERRED = [1, 2, 3, 6, 9];

class TierItemListForOverall {
  constructor(item, currentPricingDetail) {
    this.initializeFields();

    if (item === undefined) {
      this.CurrentPricingDetail = null;
      this.TierList = [];
    } else {
      this.CurrentPricingDetail = currentPricingDetail;
      this.TierList = item;
      this.Calculate();
    }
    this.Calculate = this.Calculate.bind(this);
  }

  initializeFields() {
    this.NumEvalsPerCycle = 0;
    this.DeferredCostAnnuallyFw = 0;
    this.ClientRevenueTotalInFwCurrency = 0;
    this.OtherCostTotalFw = 0;
    this.NewCostTotalCorrected = 0;
    this.FWEvalCostTotalForFieldworkCalculations = 0;
    this.EditingCostTotalForFieldworkCalculations = 0;
    this.EmployeeCostTotalForFieldworkCalculations = 0;
    this.RecruitingCostTotalForFieldworkCalculations = 0;

    // Calculated final fields used in the StatusBarCardV2
    this.RevenueTotal = 0;
    this.CostTotal = 0;
    this.CostPct = DEFAULT_PERCENT_STRING;
    this.NetRevenueTotal = 0;
    this.RevenuePct = DEFAULT_PERCENT_STRING;
    this.RevenueFWTotal = 0;
    this.CostFWTotal = 0;
    this.CostFWPct = DEFAULT_PERCENT_STRING;
    this.NetRevenueFWTotal = 0;
    this.NetRevenueFWPct = DEFAULT_PERCENT_STRING;
  }

  // Reset totals before calculation
  resetCalculationTotals() {
    this.DeferredCostAnnuallyFw = 0;
    this.ClientRevenueTotalInFwCurrency = 0;
    this.CostFWTotal = 0;
    this.CostFWPct = DEFAULT_PERCENT_STRING;
    this.NetRevenueFWTotal = 0;
    this.NetRevenueFWPct = DEFAULT_PERCENT_STRING;
    this.OtherCostTotalFw = 0;
    this.NewCostTotalCorrected = 0;
    this.FWEvalCostTotalForFieldworkCalculations = 0;
    this.EditingCostTotalForFieldworkCalculations = 0;
    this.EmployeeCostTotalForFieldworkCalculations = 0;
    this.RecruitingCostTotalForFieldworkCalculations = 0;
  }

  Calculate() {
    this.resetCalculationTotals();

    let markedUpAnnuallyTotal = 0;

    this.TierList.forEach((tier) => {
      let tempTotal = 0;

      // --- FIELDWORK OFFICE COSTS ---
      if (this.CurrentPricingDetail && tier.VPricingDetailBaseItem.OfficeId === this.CurrentPricingDetail.OfficeId) {
        // 1. Evaluation Fees
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          tier.EvalFeeAnnually +
            this.CurrentPricingDetail.EvalBonusAnnually +
            this.CurrentPricingDetail.BankChargesAnnually,
          tier.VPricingDetailBaseItem
        );

        // 2. Travel Costs
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          tier.TravelCostAnnually,
          tier.VPricingDetailBaseItem
        );

        // 3. Recruiting & Misc Costs
        const totalAnnualCosts =
          parseFloat(this.CurrentPricingDetail.AnnualRecruitingCost) +
          parseFloat(this.CurrentPricingDetail.AnnualCallsToShopperCost) +
          parseFloat(this.CurrentPricingDetail.AnnualMiscCost3) +
          parseFloat(this.CurrentPricingDetail.AnnualMiscCostPerCycle1) +
          parseFloat(this.CurrentPricingDetail.AnnualMiscCostPerCycle2) +
          parseFloat(this.CurrentPricingDetail.AnnualMiscCostPerCycle3);
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          totalAnnualCosts,
          tier.VPricingDetailBaseItem
        );

        // 4. Editing Costs
        const combinedEditorCostAnnually =
          parseFloat(this.CurrentPricingDetail.EditorCostAnnually) +
          parseFloat(this.CurrentPricingDetail.TransCostAnnually) +
          parseFloat(this.CurrentPricingDetail.ExtDataCheckAnnually);
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          combinedEditorCostAnnually,
          tier.VPricingDetailBaseItem
        );

        // 5. Commission
        let markedUpRevenuePerCycleTotalFw = 0;
        if (TierListForOverallHelper.validate(tier.MarkedUpFeePerCycle))
          markedUpRevenuePerCycleTotalFw += parseFloat(tier.MarkedUpFeePerCycle);
        const commissionPctConv = this.CurrentPricingDetail.CommissionPct / 100;
        const commissionPerCycleFw = parseFloat(markedUpRevenuePerCycleTotalFw * commissionPctConv);
        const totalCommissionForYear = parseFloat(commissionPerCycleFw * this.CurrentPricingDetail.NumCyclesPerYr);
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          totalCommissionForYear,
          tier.VPricingDetailBaseItem
        );

        // 6. Employee Costs
        const _currencyRate =
          this.CurrentPricingDetail.StatusCode === 2
            ? this.CurrentPricingDetail.FinalizedRate
            : this.CurrentPricingDetail.CurrencyRate;
        const employeeCostPerCycle =
          (this.CurrentPricingDetail.EditDataCheckSalaryPerCycle +
            this.CurrentPricingDetail.PanelMgrRecruiterSalaryPerCycle +
            this.CurrentPricingDetail.AssistantProjMgrSalaryPerCycle +
            this.CurrentPricingDetail.OperationsMgrSalaryPerCycle +
            this.CurrentPricingDetail.ProjMgrSalaryPerCycle +
            this.CurrentPricingDetail.TeamLeadSalaryPerCycle) *
          _currencyRate;
        const empBenefitsDailyCostConv = this.CurrentPricingDetail.EmpBenefitsDailyCost / 100;
        const empBenefitsPerCycleFw = parseFloat(empBenefitsDailyCostConv * employeeCostPerCycle);
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          empBenefitsPerCycleFw * this.CurrentPricingDetail.NumCyclesPerYr,
          tier.VPricingDetailBaseItem
        );

        // 7. Employee Cost Subtotal & Payroll Tax
        const employeeCostSubTotalFw =
          (this.CurrentPricingDetail.TeamLeadSalaryAnnually +
            this.CurrentPricingDetail.ProjMgrSalaryAnnually +
            this.CurrentPricingDetail.OperationsMgrSalaryAnnually +
            this.CurrentPricingDetail.AssistantProjMgrSalaryAnnually +
            this.CurrentPricingDetail.PanelMgrRecruiterSalaryAnnually +
            this.CurrentPricingDetail.EditDataCheckSalaryAnnually) *
          _currencyRate;
        const payrollTaxPctConv = this.CurrentPricingDetail.PayrollTaxPct / 100;
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          parseFloat(payrollTaxPctConv * employeeCostSubTotalFw),
          tier.VPricingDetailBaseItem
        );
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          employeeCostSubTotalFw,
          tier.VPricingDetailBaseItem
        );

        // 8. Other Costs
        let clientRevenueTotal = 0;
        if (TierListForOverallHelper.validate(tier.ClientFeeAnnually)) {
          clientRevenueTotal += tier.ClientFeeAnnually;
        }
        const overheadRatePctConvFw = this.CurrentPricingDetail.OverheadRatePct / 100;
        const overheadRateAnnuallyFw = parseFloat(clientRevenueTotal * overheadRatePctConvFw);
        let otherCostTotalFw = 0;
        if (overheadRateAnnuallyFw >= 0) otherCostTotalFw += overheadRateAnnuallyFw;
        const iCRFeeCostPerEvalConv = parseFloat(this.CurrentPricingDetail.IcrfeeCostPerEval * _currencyRate);
        const iCRFeeCostPerCycle = parseFloat(iCRFeeCostPerEvalConv * tier.NumEvalsPerCycle) || 0;
        const iCRFeeCostAnnuallyFw = parseFloat(iCRFeeCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (iCRFeeCostAnnuallyFw >= 0) otherCostTotalFw += iCRFeeCostAnnuallyFw;
        const iVRFeeCostPerEvalConv = parseFloat(this.CurrentPricingDetail.IvrfeeCostPerEval * _currencyRate);
        const iVRFeeCostPerCycle = parseFloat(iVRFeeCostPerEvalConv * tier.NumEvalsPerCycle);
        const iVRFeeCostAnnually = parseFloat(iVRFeeCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (iVRFeeCostAnnually >= 0) otherCostTotalFw += iVRFeeCostAnnually;
        const vocazillaCostPerEvalConv = parseFloat(this.CurrentPricingDetail.VocazillaCostPerEval * _currencyRate);
        const vocazillaCostPerCycle = vocazillaCostPerEvalConv * tier.NumEvalsPerCycle;
        const vocazillaCostAnnually = parseFloat(vocazillaCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (vocazillaCostAnnually >= 0) otherCostTotalFw += vocazillaCostAnnually;
        const sMCostPerEvalConv = parseFloat(this.CurrentPricingDetail.SMCostPerEval * _currencyRate);
        const sMCostAnnually = sMCostPerEvalConv * tier.NumEvalsPerCycle * this.CurrentPricingDetail.NumCyclesPerYr;
        if (sMCostAnnually >= 0) otherCostTotalFw += sMCostAnnually;
        this.OtherCostTotalFw += parseFloat(otherCostTotalFw);
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          otherCostTotalFw,
          tier.VPricingDetailBaseItem
        );

        // 9. Video Costs
        let videoCostTotalFw = 0;
        const videoEditorCostPerEval = parseFloat(
          this.CurrentPricingDetail.EditorCostPerHour * this.CurrentPricingDetail.LengthVideoShopHours
        );
        const videoEditorCostPerCycle = parseFloat(videoEditorCostPerEval * tier.NumEvalsPerCycle);
        const videoEditorCostAnnually = parseFloat(videoEditorCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (videoEditorCostAnnually >= 0) videoCostTotalFw += videoEditorCostAnnually;
        const timeCaptureCostPerEval = parseFloat(
          this.CurrentPricingDetail.TimeCaptureCostPerHour * this.CurrentPricingDetail.LengthVideoShopHours
        );
        const timeCaptureCostPerCycle = parseFloat(timeCaptureCostPerEval * tier.NumEvalsPerCycle);
        const timeCaptureCostAnnually = parseFloat(timeCaptureCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (timeCaptureCostAnnually >= 0) videoCostTotalFw += timeCaptureCostAnnually;
        const shippingCostPerCycle =
          this.CurrentPricingDetail.LengthVideoShopHours > 0
            ? parseFloat(parseFloat(this.CurrentPricingDetail.ShippingCostPerEval) * tier.NumEvalsPerCycle)
            : 0;
        const shippingCostAnnually = parseFloat(shippingCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (shippingCostAnnually >= 0) videoCostTotalFw += shippingCostAnnually;
        const cardCostPerCycle =
          this.CurrentPricingDetail.LengthVideoShopHours > 0
            ? parseFloat(this.CurrentPricingDetail.CardCostPerEval * tier.NumEvalsPerCycle)
            : 0;
        const cardCostAnnually = parseFloat(cardCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (cardCostAnnually >= 0) videoCostTotalFw += cardCostAnnually;
        const streamingCostPerEval = parseFloat(
          this.CurrentPricingDetail.StreamingCostPerHour * this.CurrentPricingDetail.LengthVideoShopHours
        );
        const streamingCostPerCycle = parseFloat(streamingCostPerEval * tier.NumEvalsPerCycle);
        const streamingCostAnnually = parseFloat(streamingCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (streamingCostAnnually >= 0) videoCostTotalFw += streamingCostAnnually;
        const cdrCostPerCycle =
          this.CurrentPricingDetail.IsVideoCdr && this.CurrentPricingDetail.LengthVideoShopHours > 0
            ? parseFloat(this.CurrentPricingDetail.CdrcostPerEval * tier.NumEvalsPerCycle)
            : 0;
        const cdrCostAnnually = parseFloat(cdrCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (cdrCostAnnually >= 0) videoCostTotalFw += cdrCostAnnually;
        const dvdCostPerCycle =
          this.CurrentPricingDetail.IsVideoDvd && this.CurrentPricingDetail.LengthVideoShopHours > 0
            ? parseFloat(this.CurrentPricingDetail.DVDCostPerEval * tier.NumEvalsPerCycle)
            : 0;
        const dvdCostAnnually = parseFloat(dvdCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (dvdCostAnnually >= 0) videoCostTotalFw += dvdCostAnnually;
        const videoCaseCost = (() => {
          // Use the same logic as TierItemList.js:GetVideoCaseCost
          const type = parseInt(this.CurrentPricingDetail.VideoCaseTypeCode);
          if (type === 1) return this.CurrentPricingDetail.CaseCostPerEval1;
          if (type === 2) return this.CurrentPricingDetail.CaseCostPerEval2;
          return 0;
        })();
        const videoCaseCostPerCycle =
          this.CurrentPricingDetail.LengthVideoShopHours > 0 ? parseFloat(videoCaseCost * tier.NumEvalsPerCycle) : 0;
        const videoCaseCostAnnually = parseFloat(videoCaseCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (videoCaseCostAnnually >= 0) videoCostTotalFw += videoCaseCostAnnually;
        const mailingCostPerCycle =
          this.CurrentPricingDetail.LengthVideoShopHours > 0
            ? parseFloat(this.CurrentPricingDetail.MailingCostPerEval * tier.NumEvalsPerCycle) || 0
            : 0;
        const mailingCostAnnually = parseFloat(mailingCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (mailingCostAnnually >= 0) videoCostTotalFw += mailingCostAnnually;
        const dneCostPerCycle =
          this.CurrentPricingDetail.LengthVideoShopHours > 0
            ? parseFloat((this.CurrentPricingDetail.Dnepct / 100) * markedUpRevenuePerCycleTotalFw)
            : 0;
        const dneCostAnnually = parseFloat(dneCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr);
        if (dneCostAnnually >= 0) videoCostTotalFw += dneCostAnnually;
        this.OtherCostTotalFw += parseFloat(videoCostTotalFw);
        tempTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          videoCostTotalFw,
          tier.VPricingDetailBaseItem
        );

        // --- END FIELDWORK OFFICE COSTS ---

        let deferredCostPerCycle = 0;
        if (this.CurrentPricingDetail.OwningOfficeIsEditing === true) {
          deferredCostPerCycle += this.CurrentPricingDetail.EditorCostPerCycle;
        }

        if (this.CurrentPricingDetail.OwningOfficeIsTranslating === true) {
          deferredCostPerCycle += this.CurrentPricingDetail.TransCostPerCycle;
        }

        if (this.CurrentPricingDetail.OwningOfficeIsChecking === true) {
          deferredCostPerCycle += this.CurrentPricingDetail.ExtDataCheckPerCycle;
        }

        deferredCostPerCycle += commissionPerCycleFw;

        if (this.StatusCode === 2 || SERVICE_TYPE_CODES_DEFERRED.includes(this.CurrentPricingDetail.ServiceTypeCode)) {
          deferredCostPerCycle += vocazillaCostPerCycle;
        }

        if (this.StatusCode === 2 || this.CurrentPricingDetail.ServiceTypeCode === 10) {
          deferredCostPerCycle += iVRFeeCostAnnually;
        }

        if (this.StatusCode === 2 || this.CurrentPricingDetail.ServiceTypeCode === 7) {
          deferredCostPerCycle += iCRFeeCostPerCycle;
        }

        if (this.CurrentPricingDetail.ServiceTypeCode !== 7 && this.CurrentPricingDetail.ServiceTypeCode !== 10) {
          const smCostPerCycle = parseFloat(sMCostPerEvalConv * tier.NumEvalsPerCycle);
          deferredCostPerCycle += smCostPerCycle;
        }

        this.DeferredCostAnnuallyFw += deferredCostPerCycle * this.CurrentPricingDetail.NumCyclesPerYr;
      }

      // If owning office and fieldwork office are the same, add to NewCostTotalCorrected
      if (tier.VPricingDetailBaseItem.OfficeId == tier.VPricingDetailBaseItem.OpportunityOfficeId) {
        this.NewCostTotalCorrected += tempTotal;
      }

      // Calculate the values, but also use the exchange rate for the given tier's pricing detail
      if (TierListForOverallHelper.validate(tier.MarkedUpAnnually)) {
        markedUpAnnuallyTotal += TierListForOverallHelper.convertToOpportunityCurrency(
          tier.MarkedUpAnnually,
          tier.VPricingDetailBaseItem
        );
      }

      if (TierListForOverallHelper.validate(tier.ClientFeeAnnually)) {
        const clientFeeAnnuallyInOppCurr = TierListForOverallHelper.convertToOpportunityCurrency(
          tier.ClientFeeAnnually,
          tier.VPricingDetailBaseItem
        );

        // If owning office and fieldwork office are not the same, add to NewCostTotalCorrected
        if (tier.VPricingDetailBaseItem.OfficeId !== tier.VPricingDetailBaseItem.OpportunityOfficeId) {
          this.NewCostTotalCorrected += clientFeeAnnuallyInOppCurr;
        }

        // Add only if related to the current pricing's fieldwork office
        if (this.CurrentPricingDetail && tier.VPricingDetailBaseItem.OfficeId === this.CurrentPricingDetail.OfficeId) {
          this.ClientRevenueTotalInFwCurrency += TierListForOverallHelper.convertToPricingCurrency(
            tier.ClientFeeAnnually,
            this.CurrentPricingDetail,
            tier.VPricingDetailBaseItem
          );
        }
      }

      const deferredCostPerCycle = TierListForOverallHelper.calculateDeferredCostPerCycle(tier);

      // If owning office and fieldwork office are not the same, add deferred cost to NewCostTotalCorrected
      if (tier.VPricingDetailBaseItem.OfficeId !== tier.VPricingDetailBaseItem.OpportunityOfficeId) {
        const deferredCostAnnually = deferredCostPerCycle * tier.VPricingDetailBaseItem.NumCyclesPerYr;
        this.NewCostTotalCorrected += TierListForOverallHelper.convertToOpportunityCurrency(
          deferredCostAnnually,
          tier.VPricingDetailBaseItem
        );
      }

      let tempCalcEvalCostTotal =
        tier.NumEvalsPerCycle * tier.EvalFeePerEval * tier.VPricingDetailBaseItem.NumCyclesPerYr;

      tempCalcEvalCostTotal +=
        (parseFloat(tier.VPricingDetailBaseItem.EvalBonusPerCycle) +
          parseFloat(tier.VPricingDetailBaseItem.BankChargesPerCycle)) *
        parseFloat(tier.VPricingDetailBaseItem.NumCyclesPerYr);

      // If owning office and fieldwork office are the same, add to FWEvalCostTotalForFieldworkCalculations
      if (this.CurrentPricingDetail.OfficeId == tier.VPricingDetailBaseItem.OfficeId) {
        this.FWEvalCostTotalForFieldworkCalculations += TierListForOverallHelper.convertToPricingCurrency(
          tempCalcEvalCostTotal,
          this.CurrentPricingDetail,
          tier.VPricingDetailBaseItem
        );
      }

      const numEvalsTotal = tier.NumEvalsPerCycle * tier.VPricingDetailBaseItem.NumCyclesPerYr;
      const tempCalcNewEditingCostTotal =
        parseFloat(numEvalsTotal) *
        ((tier.VPricingDetailBaseItem.OwningOfficeIsEditing === false
          ? parseFloat(tier.VPricingDetailBaseItem.EditorCostPerEval)
          : 0.0) +
          (tier.VPricingDetailBaseItem.OwningOfficeIsTranslating === false
            ? parseFloat(tier.VPricingDetailBaseItem.TransCostPerEval)
            : 0.0) +
          (tier.VPricingDetailBaseItem.OwningOfficeIsChecking === false
            ? parseFloat(tier.VPricingDetailBaseItem.ExtDataCheckPerEval)
            : 0.0));

      // If owning office and fieldwork office are the same, add to EditingCostTotalForFieldworkCalculations
      if (this.CurrentPricingDetail.OfficeId == tier.VPricingDetailBaseItem.OfficeId) {
        this.EditingCostTotalForFieldworkCalculations += TierListForOverallHelper.convertToPricingCurrency(
          tempCalcNewEditingCostTotal,
          this.CurrentPricingDetail,
          tier.VPricingDetailBaseItem
        );
      }

      const markedUpRevenuePerCycleTotal = parseFloat(tier.MarkedUpFeePerCycle) || 0;

      const base = tier.VPricingDetailBaseItem;
      const numCycles = base.NumCyclesPerYr || 0;
      const payrollTax = base.PayrollTaxPct || 0;
      const empBenefits = base.EmpBenefitsDailyCost || 0;
      const currencyRate = base.CurrencyRate || 1;
      const teamLead = (base.TeamLeadDailyCost || 0) * (base.TeamLeadDaysPerCycle || 0);
      const projMgr = (base.ProjMgrDailyCost || 0) * (base.ProjMgrDaysPerCycle || 0);
      const operationsMgr = (base.OperationsMgrDailyCost || 0) * (base.OperationsMgrDaysPerCycle || 0);
      const assistantProjMgr = (base.AssistantProjMgrDailyCost || 0) * (base.AssistantProjMgrDaysPerCycle || 0);
      const panelMgrRecruiter = (base.PanelMgrRecruiterDailyCost || 0) * (base.PanelMgrRecruiterDaysPerCycle || 0);
      const editDataCheck = (base.EditDataCheckDailyCost || 0) * (base.EditDataCheckDaysPerCycle || 0);
      const commissionPct = base.CommissionPct || 0;
      // Main cost sum
      const mainCost =
        numCycles *
        (1 + payrollTax / 100.0 + empBenefits / 100.0) *
        currencyRate *
        (teamLead + projMgr + operationsMgr + assistantProjMgr + panelMgrRecruiter + editDataCheck);
      // Commission part
      const commission = numCycles * (commissionPct / 100.0) * markedUpRevenuePerCycleTotal;
      // Total

      if (this.CurrentPricingDetail.OfficeId == tier.VPricingDetailBaseItem.OfficeId) {
        this.EmployeeCostTotalForFieldworkCalculations += TierListForOverallHelper.convertToPricingCurrency(
          mainCost + commission,
          this.CurrentPricingDetail,
          tier.VPricingDetailBaseItem
        );
      }

      // Recruiting & Misc Costs for FW calculations
      const tempCalcRecruitingCostTotal =
        parseFloat(base.AnnualRecruitingCost || 0) +
        parseFloat(base.AnnualCallsToShopperCost || 0) +
        parseFloat(base.AnnualMiscCost3 || 0) +
        parseFloat(base.MiscCostPerCycle1 || 0) * numCycles +
        parseFloat(base.MiscCostPerCycle2 || 0) * numCycles +
        parseFloat(base.MiscCostPerCycle3 || 0) * numCycles;

      if (this.CurrentPricingDetail.OfficeId == tier.VPricingDetailBaseItem.OfficeId) {
        this.RecruitingCostTotalForFieldworkCalculations += TierListForOverallHelper.convertToPricingCurrency(
          tempCalcRecruitingCostTotal,
          this.CurrentPricingDetail,
          tier.VPricingDetailBaseItem
        );
      }
    });

    this.MarkedUpRevenueTotal = markedUpAnnuallyTotal;

    this.finalizeDisplayedCalculations();
  }

  // Final field calculations and formatting
  finalizeDisplayedCalculations() {
    // Get the first tier's VPricingDetailBaseItem's OpportunityCurrencyCode for formatting
    const currencyCode =
      this.TierList.length > 0 ? this.TierList[0].VPricingDetailBaseItem.OpportunityCurrencyCode : '';

    // Round revenue and cost to 2 decimals first, so NET = REVENUE - COST is arithmetically consistent
    const roundedRevenue = mathHelper.RoundToTwoDecimal(this.MarkedUpRevenueTotal);
    const roundedCost = mathHelper.RoundToTwoDecimal(this.NewCostTotalCorrected);
    const roundedNet = mathHelper.RoundToTwoDecimal(roundedRevenue - roundedCost);

    // Used as TOTAL OF ALL REVENUE
    this.RevenueTotal = moneyHelper.toMoneyFormat(roundedRevenue, currencyCode);

    // Used as TOTAL OF ALL COSTS
    this.CostTotal = moneyHelper.toMoneyFormat(roundedCost, currencyCode);

    // Used as TOTAL OF ALL COSTS percentage
    this.CostPct = mathHelper.PercentageText(roundedCost, roundedRevenue);

    // Used as NET OF ALL REVENUE
    this.NetRevenueTotal = moneyHelper.toMoneyFormat(roundedNet, currencyCode);

    // Used as NET OF ALL REVENUE percentage
    this.RevenuePct = mathHelper.PercentageText(roundedNet, roundedRevenue);
    const pricingCurrencyCode = this.CurrentPricingDetail.CurrencyCode || '';

    const formattedClientRevenue = mathHelper.RoundToTwoDecimal(this.ClientRevenueTotalInFwCurrency);

    // Used as TOTAL OF ALL FW REVENUE
    this.RevenueFWTotal = moneyHelper.toMoneyFormat(formattedClientRevenue, pricingCurrencyCode);

    // Calculation of TOTAL OF ALL FW COSTS
    const totalFieldworkCost = mathHelper.RoundToTwoDecimal(
      this.FWEvalCostTotalForFieldworkCalculations +
      this.EditingCostTotalForFieldworkCalculations +
      this.EmployeeCostTotalForFieldworkCalculations +
      this.RecruitingCostTotalForFieldworkCalculations +
      (this.OtherCostTotalFw - this.DeferredCostAnnuallyFw)
    );

    // Used as TOTAL OF ALL FW COSTS
    this.CostFWTotal = moneyHelper.toMoneyFormat(totalFieldworkCost, pricingCurrencyCode);

    // Used as TOTAL OF ALL FW COSTS percentage
    this.CostFWPct = mathHelper.PercentageText(totalFieldworkCost, formattedClientRevenue);

    // Used as NET OF ALL FW REVENUE
    this.NetRevenueFWTotal = moneyHelper.toMoneyFormat(
      formattedClientRevenue - totalFieldworkCost,
      pricingCurrencyCode
    );

    // Used as NET OF ALL FW REVENUE percentage
    this.NetRevenueFWPct = mathHelper.PercentageText(
      formattedClientRevenue - totalFieldworkCost,
      formattedClientRevenue
    );
  }
}

export default TierItemListForOverall;
